import { FormControl, Grid, VStack } from '@chakra-ui/react'
import { Controller, useFormContext } from 'react-hook-form'
import { type CountryCode } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { overrideText } from 'ui/lib/overrides'
import { NshiftFormFieldLabel } from '../NshiftCheckout/NshiftFormField'
import { CheckoutCountrySelect } from '../CheckoutCountrySelect'
import { type PostNordCheckoutFormValues } from './types'
import { PostNordFormField } from './PostNordFormField'
import { type PostNordOverrides } from './translations'

interface PostNordAddressProps {
  isPostalCodeLoading: boolean
  postalCodePattern?: RegExp
  isRequiredStreetAddress?: boolean
  availableCountries?: CountryCode[]
}

export const PostNordAddress = ({
  isPostalCodeLoading,
  postalCodePattern,
  isRequiredStreetAddress,
  availableCountries
}: PostNordAddressProps) => {
  const overrides = useOverridesContext<keyof PostNordOverrides>()
  const form = useFormContext<PostNordCheckoutFormValues>()

  return (
    <VStack spacing={4} align="stretch" w="100%" id="postnord-address-form">
      <Grid
        gap={4}
        templateColumns={{
          base: '1fr',
          sm: '1fr 2fr'
        }}
      >
        <PostNordFormField
          name="postCode"
          label={overrideText('Postnummer', overrides?.fieldPostalCodeLabel)}
          isLoading={isPostalCodeLoading}
          formControlProps={{
            isRequired: true
          }}
          controllerProps={{
            rules: {
              required: {
                value: true,
                message: overrideText('Obligatoriskt', overrides?.errorRequired)
              },
              ...(postalCodePattern
                ? {
                    pattern: {
                      value: postalCodePattern,
                      message: overrideText('Ogiltigt format', overrides?.errorInvalidFormat)
                    }
                  }
                : {})
            }
          }}
        />

        <Controller
          name="countryCode"
          render={({ field }) => (
            <FormControl isDisabled={false}>
              <NshiftFormFieldLabel>
                {overrideText('Land', overrides?.fieldCountryCodeLabel)}
              </NshiftFormFieldLabel>

              <CheckoutCountrySelect
                instanceId="postnord-country-select"
                availableCountries={availableCountries}
                defaultValue={form.formState.defaultValues?.countryCode}
                value={field.value}
                selectProps={{
                  onChange: (newValue) => {
                    field.onChange(newValue!.value)
                  },
                  onBlur: field.onBlur
                }}
              />
            </FormControl>
          )}
        />
      </Grid>

      <PostNordFormField
        name="city"
        label={overrideText('Stad', overrides?.fieldCityLabel)}
        formControlProps={{
          isRequired: true
        }}
        controllerProps={{
          rules: {
            required: {
              value: true,
              message: overrideText('Obligatoriskt', overrides?.errorRequired)
            }
          }
        }}
      />

      <Grid
        gap={4}
        templateColumns={{
          base: '1fr',
          sm: '2fr 1fr'
        }}
      >
        <PostNordFormField
          name="streetName"
          label={overrideText('Gatunamn', overrides?.fieldStreetNameLabel)}
          formControlProps={{
            isRequired: isRequiredStreetAddress
          }}
          controllerProps={{
            rules: {
              required: {
                value: !!isRequiredStreetAddress,
                message: overrideText('Obligatoriskt', overrides?.errorRequired)
              }
            }
          }}
        />

        <PostNordFormField
          name="streetNumber"
          label={overrideText('Gatunummer', overrides?.fieldStreetNumberLabel)}
          formControlProps={{
            isRequired: isRequiredStreetAddress
          }}
          controllerProps={{
            rules: {
              required: {
                value: !!isRequiredStreetAddress,
                message: overrideText('Obligatoriskt', overrides?.errorRequired)
              }
            }
          }}
        />
      </Grid>
    </VStack>
  )
}
